import React, { useEffect, useState } from 'react';
import { Input } from '@flixbus/honeycomb-react';
import { useVoucherFormContext } from '../../Context';
import { AllowedCustomerRule } from '../../../../types/Rules';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+\d{7,15}$/;
const ACCOUNT_ID_REGEX = /^[a-zA-Z0-9-]{1,128}$/;

const AllowedCustomerRuleEditor: React.FC = () => {
  const {
    formState: {
      payload: { rules },
    },
    handleRuleChange,
    setFormError,
  } = useVoucherFormContext();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [accountId, setAccountId] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [accountIdTouched, setAccountIdTouched] = useState(false);

  useEffect(() => {
    const existingRule = rules.find(r => r.type === 'allowed_customer') as
      | AllowedCustomerRule
      | undefined;
    setEmail(existingRule?.identifiers.find(i => i.type === 'email')?.value ?? '');
    setPhone(existingRule?.identifiers.find(i => i.type === 'phone')?.value ?? '');
    setAccountId(existingRule?.identifiers.find(i => i.type === 'account_id')?.value ?? '');
  }, [rules]);

  const updateRule = (emailValue: string, phoneValue: string, accountIdValue: string) => {
    const identifiers: AllowedCustomerRule['identifiers'] = [];
    if (emailValue.trim() && EMAIL_REGEX.test(emailValue.trim())) {
      identifiers.push({ type: 'email', value: emailValue.trim() });
    }
    if (phoneValue.trim() && PHONE_REGEX.test(phoneValue.trim())) {
      identifiers.push({ type: 'phone', value: phoneValue.trim() });
    }
    if (accountIdValue.trim() && ACCOUNT_ID_REGEX.test(accountIdValue.trim())) {
      identifiers.push({ type: 'account_id', value: accountIdValue.trim() });
    }
    handleRuleChange({ type: 'allowed_customer', identifiers }, identifiers.length === 0);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (value.trim() === '' || EMAIL_REGEX.test(value.trim())) {
      setFormError('allowed_customer_email', null);
      updateRule(value, phone, accountId);
    } else {
      setFormError('allowed_customer_email', 'Please enter a valid email address');
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhone(value);

    if (value.trim() === '' || PHONE_REGEX.test(value.trim())) {
      setFormError('allowed_customer_phone', null);
      updateRule(email, value, accountId);
    } else {
      setFormError(
        'allowed_customer_phone',
        'Please enter a valid phone number (e.g. +1123456789)',
      );
    }
  };

  const handleAccountIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAccountId(value);

    if (value.trim() === '' || ACCOUNT_ID_REGEX.test(value.trim())) {
      setFormError('allowed_customer_account_id', null);
      updateRule(email, phone, value);
    } else {
      setFormError('allowed_customer_account_id', 'Please enter a valid account ID');
    }
  };

  const isEmailInvalid = emailTouched && email.trim() !== '' && !EMAIL_REGEX.test(email.trim());
  const isPhoneInvalid = phoneTouched && phone.trim() !== '' && !PHONE_REGEX.test(phone.trim());
  const isAccountIdInvalid =
    accountIdTouched && accountId.trim() !== '' && !ACCOUNT_ID_REGEX.test(accountId.trim());

  return (
    <>
      <Input
        id='allowed-customer-email'
        name='allowed-customer-email'
        label='Customer Email'
        type='email'
        value={email}
        onChange={handleEmailChange}
        onBlur={() => setEmailTouched(true)}
        valid={isEmailInvalid ? false : undefined}
        infoError='Please enter a valid email address'
        info='The voucher can only be redeemed by this customer'
      />
      <Input
        id='allowed-customer-phone'
        name='allowed-customer-phone'
        label='Customer Phone'
        type='tel'
        value={phone}
        onChange={handlePhoneChange}
        onBlur={() => setPhoneTouched(true)}
        valid={isPhoneInvalid ? false : undefined}
        infoError='Please enter a valid phone number (e.g. +1123456789)'
        info='Country code + phone number (e.g. +1123456789)'
      />
      <Input
        id='allowed-customer-account-id'
        name='allowed-customer-account-id'
        label='Customer Account ID'
        type='text'
        value={accountId}
        onChange={handleAccountIdChange}
        onBlur={() => setAccountIdTouched(true)}
        valid={isAccountIdInvalid ? false : undefined}
        infoError='Please enter a valid account ID'
        info='Flix customer ID (UUID from account-service)'
      />
    </>
  );
};

export default AllowedCustomerRuleEditor;
